import React, { useEffect, useState } from 'react';
import { LEGAL_CONTACT } from './policyContent';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
const GRIEVANCE_SUCCESS_MSG = 'Grievance submitted. You will receive acknowledgement within 24 hours.';

function isGrievancePage(slug) {
  if (slug === 'grievance') return true;
  if (typeof window !== 'undefined' && window.location.pathname === '/grievance') return true;
  return false;
}

function GrievanceForm() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [bookingRef, setBookingRef] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    try {
      const token = localStorage.getItem('developmentexpress_token');
      if (!token) return;
      const user = JSON.parse(localStorage.getItem('developmentexpress_user') || '{}');
      if (user.name) setName(String(user.name).trim());
      if (user.email) setEmail(String(user.email).trim().toLowerCase());
    } catch (_err) {
      // ignore corrupt localStorage
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setSuccessMsg('');
    setErrorMsg('');
    setLoading(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = localStorage.getItem('developmentexpress_token');
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(`${API_BASE_URL}/api/public/grievances`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim().toLowerCase(),
          subject: subject.trim(),
          description: description.trim(),
          booking_ref: bookingRef.trim() || undefined,
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (response.status === 201 && payload?.success) {
        setSuccessMsg(payload.message || GRIEVANCE_SUCCESS_MSG);
        setSubject('');
        setDescription('');
        setBookingRef('');
        return;
      }
      setErrorMsg(payload.error || payload.message || 'Could not submit grievance. Please try again.');
    } catch (_err) {
      setErrorMsg('Could not submit grievance. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section style={s.grievanceSection}>
      <h2 style={s.grievanceHeading}>Submit a Grievance</h2>
      <p style={s.grievanceNote}>
        All grievances are acknowledged within 24 hours and resolved within 7 days.
      </p>
      {successMsg && <p style={s.successMsg}>{successMsg}</p>}
      {errorMsg && <p style={s.errorMsg}>{errorMsg}</p>}
      <form onSubmit={handleSubmit} style={s.form}>
        <label style={s.label}>
          Name
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            style={s.input}
            disabled={loading}
          />
        </label>
        <label style={s.label}>
          Email
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={120}
            style={s.input}
            disabled={loading}
          />
        </label>
        <label style={s.label}>
          Subject
          <input
            type="text"
            required
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={120}
            style={s.input}
            disabled={loading}
          />
        </label>
        <label style={s.label}>
          Description
          <textarea
            required
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
            rows={5}
            style={s.textarea}
            disabled={loading}
          />
        </label>
        <label style={s.label}>
          Booking Reference (optional)
          <input
            type="text"
            value={bookingRef}
            onChange={(e) => setBookingRef(e.target.value)}
            maxLength={32}
            style={s.input}
            disabled={loading}
          />
        </label>
        <button type="submit" style={s.submitBtn} disabled={loading}>
          {loading ? 'Submitting…' : 'Submit Grievance'}
        </button>
      </form>
    </section>
  );
}

export default function LegalPageShell({ title, meta, children, slug }) {
  return (
    <div style={s.page}>
      <header style={s.header}>
        <p style={s.brand}>{LEGAL_CONTACT.company} — {LEGAL_CONTACT.platform}</p>
        <h1 style={s.title}>{title}</h1>
        <p style={s.meta}>{meta || `Last updated: June 2026`}</p>
        <div style={s.contactBox}>
          <strong style={{ color: '#c9a84c' }}>Contact:</strong>{' '}
          {LEGAL_CONTACT.email} · {LEGAL_CONTACT.phone} · {LEGAL_CONTACT.address}
        </div>
      </header>
      {children}
      {isGrievancePage(slug) && <GrievanceForm />}
      <footer style={s.footer}>
        <a href={LEGAL_CONTACT.marketing} style={s.link} rel="noopener noreferrer">
          developmentexpress.in
        </a>
        <span style={s.sep}> · </span>
        <a href="/privacy" style={s.link}>Privacy</a>
        <span style={s.sep}> · </span>
        <a href="/delete-account" style={s.link}>Delete account</a>
        <span style={s.sep}> · </span>
        <a href="/grievance" style={s.link}>Grievance</a>
      </footer>
    </div>
  );
}

const s = {
  page: {
    maxWidth: '720px',
    margin: '0 auto',
    padding: 'calc(env(safe-area-inset-top, 0px) + 24px) max(16px, env(safe-area-inset-right, 0px)) calc(env(safe-area-inset-bottom, 0px) + 40px) max(16px, env(safe-area-inset-left, 0px))',
    boxSizing: 'border-box',
    width: '100%',
    overflowX: 'hidden',
    fontFamily: 'Arial, sans-serif',
    color: '#e8e0d0',
    background: '#050d1a',
    minHeight: '100dvh',
    lineHeight: 1.55,
  },
  grievanceSection: {
    marginTop: '32px',
    padding: '20px',
    background: '#030810',
    border: '1px solid rgba(201,168,76,0.3)',
    borderRadius: '8px',
    boxSizing: 'border-box',
  },
  grievanceHeading: { color: '#C9A84C', fontSize: '14px', margin: '0 0 8px' },
  grievanceNote: { color: '#8896a8', fontSize: '12px', margin: '0 0 16px' },
  form: { display: 'flex', flexDirection: 'column', gap: '12px' },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    color: '#8896a8',
    fontSize: '12px',
  },
  input: {
    background: 'rgba(15,32,64,0.8)',
    border: '1px solid rgba(201,168,76,0.3)',
    borderRadius: '8px',
    color: '#e8e0d0',
    fontSize: '13px',
    padding: '8px 12px',
    outline: 'none',
    boxSizing: 'border-box',
    width: '100%',
  },
  textarea: {
    background: 'rgba(15,32,64,0.8)',
    border: '1px solid rgba(201,168,76,0.3)',
    borderRadius: '8px',
    color: '#e8e0d0',
    fontSize: '13px',
    padding: '8px 12px',
    outline: 'none',
    boxSizing: 'border-box',
    width: '100%',
    resize: 'vertical',
    minHeight: '100px',
  },
  submitBtn: {
    marginTop: '4px',
    padding: '10px 16px',
    background: 'linear-gradient(135deg, #c9a84c, #8b6914)',
    color: '#0a1628',
    border: 'none',
    borderRadius: '8px',
    fontWeight: '700',
    fontSize: '13px',
    cursor: 'pointer',
    alignSelf: 'flex-start',
  },
  successMsg: { color: '#c9a84c', fontSize: '13px', margin: '0 0 12px' },
  errorMsg: { color: '#e8a0a0', fontSize: '13px', margin: '0 0 12px' },
  header: { marginBottom: '28px', borderBottom: '1px solid rgba(201,168,76,0.25)', paddingBottom: '16px' },
  brand: { color: '#c9a84c', fontSize: '12px', margin: '0 0 8px', letterSpacing: '0.05em' },
  title: { color: '#c9a84c', fontSize: '22px', margin: '0 0 8px' },
  meta: { color: '#8896a8', fontSize: '11px', margin: '0 0 12px' },
  contactBox: { color: '#8896a8', fontSize: '11px', lineHeight: 1.5, margin: 0 },
  footer: { marginTop: '32px', fontSize: '12px' },
  link: { color: '#c9a84c' },
  sep: { color: '#556' },
};
